import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { BuildingsService } from 'src/app/services/buildings.service';
import { UsersService } from 'src/app/services/users.service';
import { ToastService } from 'src/app/services/toast.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-building-reassignment',
  templateUrl: './building-reassignment.component.html',
  styleUrls: ['./building-reassignment.component.scss'],
})
export class BuildingReassignmentComponent implements OnInit, OnDestroy {
  @Output() goBack = new EventEmitter<void>();

  // Building workflow properties
  selectedNewBuilding: any = null;
  selectedFilterBuilding: any = null;
  assignmentType: 'move' | 'add' = 'move';

  // User list properties
  userList: any[] = [];
  filteredUserList: any[] = [];
  selectedUsers: any[] = [];
  userSearchText = '';
  selectAllUsers = false;

  // Buildings properties
  buildings: any[] = [];

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(
    private http: HttpClient,
    private buildingsService: BuildingsService,
    private usersService: UsersService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loadBuildings();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onBackClick(): void {
    this.reset();
    this.goBack.emit();
  }

  // Load buildings
  loadBuildings = () => {
    const buildingSubscription = this.buildingsService.buildingsData$.subscribe(
      (buildingData: any) => {
        if (buildingData?.rows) {
          this.buildings = buildingData.rows;
        }
      }
    );
    this.subscriptions.push(buildingSubscription);

    // Get active buildings
    this.buildingsService.getBuildings(1000, 0);
  };

  // Building selection methods
  onNewBuildingSelected = (building: any) => {
    this.selectedNewBuilding = building;
  };

  onFilterBuildingSelected = (building: any) => {
    this.selectedFilterBuilding = building;
    this.loadStaffFromBuilding(building);
  };

  loadStaffFromBuilding = (building: any) => {
    this.userList = [];
    this.filteredUserList = [];

    const buildingSubscription = this.buildingsService
      .getBuildingDetails(building.id, 'Users, UserCount')
      .subscribe({
        next: (response: any) => {
          if (response?.data?.Users && Array.isArray(response.data.Users)) {
            const activeUsers = response.data.Users.filter(
              (user: any) => user.activeFlag === 1
            );

            this.userList = activeUsers;
            this.filteredUserList = [...this.userList];
          } else {
            this.userList = [];
            this.filteredUserList = [];
          }
        },
        error: (error: any) => {
          console.error('Error fetching building details:', error);
          this.userList = [];
          this.filteredUserList = [];
        },
      });

    this.subscriptions.push(buildingSubscription);
  };

  // User search functionality
  onUserSearchChange = (searchText: string) => {
    this.userSearchText = searchText;

    if (!searchText || searchText.trim() === '') {
      this.filteredUserList = [...this.userList];
    } else {
      this.filteredUserList = this.userList.filter(
        (user) =>
          user.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.lastName?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
  };

  // User selection methods
  toggleUserSelection = (user: any) => {
    const index = this.selectedUsers.findIndex((u) => u.id === user.id);
    if (index > -1) {
      this.selectedUsers.splice(index, 1);
    } else {
      this.selectedUsers.push(user);
    }
  };

  toggleSelectAllUsers = () => {
    this.selectAllUsers = !this.selectAllUsers;
    if (this.selectAllUsers) {
      this.selectedUsers = [...this.filteredUserList];
    } else {
      this.selectedUsers = [];
    }
  };

  isUserSelected = (user: any) => {
    return this.selectedUsers.some((u) => u.id === user.id);
  };

  // Assign building to selected users
  assignBuildingToUsers = () => {
    if (this.selectedUsers.length === 0 || !this.selectedNewBuilding) {
      this.toastService.setToast({
        text: 'Please select both users and a building to assign.',
        type: 'warn',
        icon: true,
      });
      return;
    }

    const userIds = this.selectedUsers.map((user: any) => user.id);

    if (this.assignmentType === 'move') {
      const currentBuildingId = this.selectedFilterBuilding?.id || 0;

      const moveRequest = this.http.put(
        `${environment.expressUrl}/maintenance/buildings/reassign-users`,
        {
          loginName: this.usersService.user.loginName,
          maintainBuildingAssignment: false,
          overwritePrimaryBuilding: true,
          userIds: userIds.map((id: any) => parseFloat(id)),
          curBuildingId: parseFloat(currentBuildingId),
          newBuildingId: parseFloat(this.selectedNewBuilding.id),
        },
        { headers: { 'Content-Type': 'application/vnd.api+json' } }
      );

      moveRequest.subscribe({
        next: (response: any) => {
          this.toastService.setToast({
            text: `Successfully moved ${this.selectedUsers.length} staff member(s) to "${this.selectedNewBuilding.buildingName}"`,
            type: 'success',
          });

          this.clearSelectionsAndRefresh();
        },
        error: (error: any) => {
          console.error('Error moving users to building:', error);
          this.toastService.setToast({
            text: 'Error moving staff to building. Please try again.',
            type: 'error',
          });
        },
      });
    } else {
      const currentBuildingId = this.selectedFilterBuilding?.id || 0;

      const addRequest = this.http.put(
        `${environment.expressUrl}/maintenance/buildings/reassign-users`,
        {
          loginName: this.usersService.user.loginName,
          maintainBuildingAssignment: true,
          overwritePrimaryBuilding: false,
          userIds: userIds.map((id: any) => parseFloat(id)),
          curBuildingId: parseFloat(currentBuildingId),
          newBuildingId: parseFloat(this.selectedNewBuilding.id),
        },
        { headers: { 'Content-Type': 'application/vnd.api+json' } }
      );

      addRequest.subscribe({
        next: (response: any) => {
          this.toastService.setToast({
            text: `Successfully added ${this.selectedUsers.length} staff member(s) to "${this.selectedNewBuilding.buildingName}"`,
            type: 'success',
          });

          this.clearSelectionsAndRefresh();
        },
        error: (error: any) => {
          console.error('Error adding users to building:', error);
          this.toastService.setToast({
            text: 'Error adding staff to building. Please try again.',
            type: 'error',
          });
        },
      });
    }
  };

  clearSelectionsAndRefresh = () => {
    this.selectedUsers = [];
    this.selectAllUsers = false;

    if (this.selectedFilterBuilding) {
      this.loadStaffFromBuilding(this.selectedFilterBuilding);
    }
  };

  reset = () => {
    this.selectedNewBuilding = null;
    this.selectedFilterBuilding = null;
    this.userList = [];
    this.filteredUserList = [];
    this.selectedUsers = [];
    this.userSearchText = '';
    this.selectAllUsers = false;
    this.buildings = [];
  };
}

